'use client';

import { useCallback, useContext } from 'react';
import { ApolloClient, ApolloLink, getApolloContext, InMemoryCache, Observable } from '@apollo/client';

import { useLazyQueryWithErrorLogging } from '@savvy/graphql-hooks/useLazyQueryWithErrorLogging';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { SavedViewEntries, SavedViewValue } from './remote-user-saved-views';
import { upsertUserSavedViewsMutation, userSavedViewsQuery } from './remote-user-saved-views';

type UsePersistedViewResult = {
  hasRemotePersistence: boolean;
  getSavedViewsByKeys: (params: { keys: string[] }) => Promise<Map<string, SavedViewValue>>;
  upsertSavedViews: (params: { entries: SavedViewEntries }) => Promise<void>;
};

const noopApolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: new ApolloLink(() => new Observable((observer) => observer.complete())),
});

export function usePersistedView(): UsePersistedViewResult {
  const apolloContext = useContext(getApolloContext());
  const apolloClient = apolloContext.client ?? null;
  const client = apolloClient ?? noopApolloClient;
  const [getSavedViewsByKeysQuery] = useLazyQueryWithErrorLogging(userSavedViewsQuery, { client });
  const [upsertSavedViewsMutation] = useMutationWithErrorLogging(upsertUserSavedViewsMutation, { client });

  const getSavedViewsByKeys = useCallback(
    async ({ keys }: { keys: string[] }) => {
      if (apolloClient === null) return new Map<string, SavedViewValue>();
      if (keys.length === 0) return new Map<string, SavedViewValue>();

      const response = await getSavedViewsByKeysQuery({
        variables: { keys },
        fetchPolicy: 'network-only',
      });
      const userSavedViews = response.data?.userSavedViews ?? [];
      return new Map<string, SavedViewValue>(userSavedViews.map((item) => [item.key, item.value]));
    },
    [apolloClient, getSavedViewsByKeysQuery],
  );

  const upsertSavedViews = useCallback(
    async ({ entries }: { entries: SavedViewEntries }) => {
      if (apolloClient === null) return;
      if (entries.length === 0) return;

      await upsertSavedViewsMutation({
        variables: {
          input: {
            savedViews: entries,
          },
        },
      });
    },
    [apolloClient, upsertSavedViewsMutation],
  );

  return {
    hasRemotePersistence: apolloClient !== null,
    getSavedViewsByKeys,
    upsertSavedViews,
  };
}
